import { cn } from "@/lib/utils";
import { useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/**
 * Shared Parent Hub glass-holo primitives. These are the visual benchmark
 * for the Parent Hub upgrade — frosted dark glass, soft holo edge, gentle
 * inner glow. Keep usage consistent so panels feel native to the hub.
 */

/**
 * Parent Hub surface tokens — neutral graphite glass system.
 *
 * Replaces the previous purple-tinted glass to fix "purple-on-purple
 * sameness". Tonal separation now comes from THREE distinct surface
 * tiers + an optional accent rim, not from saturating every card with
 * primary.
 *
 *   PARENT_HERO   → Intelligence panel (use once per tab, real depth)
 *   PARENT_GLASS  → Standard analytics panel (quieter graphite glass)
 *   PARENT_GLASS_QUIET → Inset / secondary surfaces (no shadow, no glow)
 *
 * Every tier uses a double-stroke edge (outer hairline + inset highlight)
 * for refined, expensive-feeling rims without harsh outlines.
 */
export const PARENT_GLASS =
  "relative overflow-hidden rounded-2xl border border-white/[0.06] " +
  "bg-[linear-gradient(180deg,hsl(var(--card)/0.72)_0%,hsl(var(--card)/0.48)_100%)] " +
  "backdrop-blur-2xl " +
  "ring-1 ring-inset ring-white/[0.03] " +
  "shadow-[0_18px_50px_-32px_rgba(0,0,0,0.65),0_2px_8px_-4px_rgba(0,0,0,0.35),inset_0_1px_0_0_hsl(var(--foreground)/0.06)]";

export const PARENT_GLASS_QUIET =
  "relative overflow-hidden rounded-2xl border border-white/[0.04] " +
  "bg-card/40 backdrop-blur-xl ring-1 ring-inset ring-white/[0.02]";

/**
 * Signature glass for the Primary Intelligence panel. ONE per tab.
 * Layered cool accent rim (not a heavy purple wash) + deeper shadow stack.
 */
export const PARENT_HERO =
  "relative overflow-hidden rounded-[28px] border border-white/[0.08] " +
  "bg-[linear-gradient(180deg,hsl(var(--card)/0.78)_0%,hsl(var(--card)/0.52)_100%)] " +
  "backdrop-blur-2xl " +
  "ring-1 ring-inset ring-white/[0.04] " +
  "shadow-[0_44px_100px_-50px_rgba(0,0,0,0.75),0_4px_14px_-6px_rgba(0,0,0,0.45),inset_0_1px_0_0_hsl(var(--foreground)/0.08)]";

export function ParentHeroPanel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn(PARENT_HERO, className)}>
      {/* Platinum hairline + restrained cool accent — no more purple wash */}
      <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      <div className="pointer-events-none absolute -top-32 right-1/4 h-64 w-64 rounded-full bg-cyan-500/[0.05] blur-[100px]" />
      <div className="relative">{children}</div>
    </section>
  );
}

export function ParentGlassCard({
  children,
  className,
  glow = true,
  quiet = false,
}: {
  children: React.ReactNode;
  className?: string;
  glow?: boolean;
  quiet?: boolean;
}) {
  return (
    <section className={cn(quiet ? PARENT_GLASS_QUIET : PARENT_GLASS, className)}>
      {glow && !quiet && (
        <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/[0.12] to-transparent" />
      )}
      <div className="relative">{children}</div>
    </section>
  );
}

/* ----------------------------------------------------------------- */
/* Signal Tile — compact metric card. Short labels only. No truncation. */
/* ----------------------------------------------------------------- */

export function ParentSignalTile({
  icon: Icon,
  label,
  value,
  hint,
  tone = "neutral",
  accent = "cyan",
}: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: ChipTone;
  accent?: AccentKey;
}) {
  const cls = ACCENT_CLS[accent];
  return (
    <div
      className={cn(
        "group relative flex h-full min-h-[92px] flex-col justify-between overflow-hidden rounded-2xl",
        "border border-white/[0.07] ring-1 ring-inset ring-white/[0.03]",
        "bg-[linear-gradient(180deg,hsl(var(--card)/0.7)_0%,hsl(var(--card)/0.45)_100%)] px-4 py-3.5 backdrop-blur-xl",
        "shadow-[0_10px_28px_-22px_rgba(0,0,0,0.6),inset_0_1px_0_0_hsl(var(--foreground)/0.06)]",
        "transition-all hover:border-white/[0.11] hover:ring-white/[0.06]",
      )}
    >
      <div className={cn("pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent to-transparent", cls.hairline)} />
      <div className="relative flex items-center gap-2.5">
        {Icon && (
          <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ring-1", cls.chip)}>
            <Icon className="h-3.5 w-3.5" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground/85">{label}</p>
          <div className="mt-1 font-heading text-[17px] font-semibold tracking-tight text-foreground sm:text-[19px] leading-[1.1]">
            {value}
          </div>
        </div>
      </div>
      <div className="relative mt-2 flex items-center justify-between gap-2">
        {hint ? (
          <p className="text-[10.5px] text-muted-foreground/80 leading-snug truncate">{hint}</p>
        ) : <span />}
        {tone !== "neutral" && (
          <ParentStatusChip tone={tone} label={statusLabelForTone(tone)} />
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- */
/* Accent system — semantic color allocation across the Parent Hub.   */
/* Cyan = intelligence/AI/path · Emerald = growth/strong              */
/* Amber = parent attention/opportunity · Rose = true risk            */
/* Violet = controlled premium accent ONLY                             */
/* ----------------------------------------------------------------- */

export type AccentKey = "cyan" | "emerald" | "amber" | "rose" | "violet" | "neutral";

export const ACCENT_CLS: Record<AccentKey, { chip: string; text: string; ring: string; bg: string; hairline: string; dot: string }> = {
  cyan: {
    chip: "text-cyan-300 bg-cyan-500/10 ring-cyan-400/25",
    text: "text-cyan-300",
    ring: "ring-cyan-400/30",
    bg: "bg-cyan-500/10",
    hairline: "via-cyan-400/25",
    dot: "bg-cyan-400",
  },
  emerald: {
    chip: "text-emerald-300 bg-emerald-500/10 ring-emerald-400/25",
    text: "text-emerald-300",
    ring: "ring-emerald-400/30",
    bg: "bg-emerald-500/10",
    hairline: "via-emerald-400/25",
    dot: "bg-emerald-400",
  },
  amber: {
    chip: "text-amber-200 bg-amber-500/10 ring-amber-400/25",
    text: "text-amber-200",
    ring: "ring-amber-400/30",
    bg: "bg-amber-500/10",
    hairline: "via-amber-400/25",
    dot: "bg-amber-300",
  },
  rose: {
    chip: "text-rose-300 bg-rose-500/10 ring-rose-400/25",
    text: "text-rose-300",
    ring: "ring-rose-400/30",
    bg: "bg-rose-500/10",
    hairline: "via-rose-400/25",
    dot: "bg-rose-400",
  },
  violet: {
    chip: "text-violet-200 bg-violet-500/10 ring-violet-400/25",
    text: "text-violet-200",
    ring: "ring-violet-400/30",
    bg: "bg-violet-500/10",
    hairline: "via-violet-400/25",
    dot: "bg-violet-400",
  },
  neutral: {
    chip: "text-foreground/85 bg-white/[0.04] ring-white/10",
    text: "text-foreground/85",
    ring: "ring-white/10",
    bg: "bg-white/[0.04]",
    hairline: "via-white/15",
    dot: "bg-foreground/60",
  },
};

/* ----------------------------------------------------------------- */
/* Expandable signal panel — drilldown intelligence card.             */
/* ----------------------------------------------------------------- */

export function ParentExpandableSignal({
  icon: Icon,
  label,
  value,
  meaning,
  accent = "cyan",
  tone,
  children,
  defaultOpen = false,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value: ReactNode;
  meaning: string;
  accent?: AccentKey;
  tone?: { label: string; accent: AccentKey };
  children?: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const cls = ACCENT_CLS[accent];
  const hasDetail = Boolean(children);
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border border-white/[0.07] ring-1 ring-inset ring-white/[0.03]",
        "bg-[linear-gradient(180deg,hsl(var(--card)/0.7)_0%,hsl(var(--card)/0.45)_100%)] backdrop-blur-xl",
        "shadow-[0_12px_30px_-22px_rgba(0,0,0,0.6),inset_0_1px_0_0_hsl(var(--foreground)/0.05)]",
        "transition hover:border-white/[0.11]",
      )}
    >
      <div className={cn("pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent to-transparent", cls.hairline)} />
      <button
        type="button"
        onClick={() => hasDetail && setOpen((v) => !v)}
        className={cn("relative flex w-full items-start gap-3 px-4 py-3.5 text-left", hasDetail ? "cursor-pointer" : "cursor-default")}
        aria-expanded={hasDetail ? open : undefined}
      >
        {Icon && (
          <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1", cls.chip)}>
            <Icon className="h-4 w-4" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground/85">{label}</p>
            {tone && (
              <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium ring-1", ACCENT_CLS[tone.accent].chip)}>
                <span className={cn("h-1 w-1 rounded-full", ACCENT_CLS[tone.accent].dot)} />
                {tone.label}
              </span>
            )}
          </div>
          <div className="mt-1 flex items-end justify-between gap-2">
            <div className="font-heading text-[18px] font-semibold tracking-tight text-foreground leading-[1.1] sm:text-[20px]">
              {value}
            </div>
            {hasDetail && (
              <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
            )}
          </div>
          <p className="mt-1.5 text-[11.5px] leading-snug text-muted-foreground/85">{meaning}</p>
        </div>
      </button>
      {hasDetail && open && (
        <div className="relative border-t border-white/[0.06] bg-background/30 px-4 py-3 text-[12px] leading-relaxed text-foreground/90">
          {children}
        </div>
      )}
    </div>
  );
}

function statusLabelForTone(t: ChipTone): string {
  switch (t) {
    case "ready":
      return "Strong";
    case "growth":
      return "Building";
    case "watch":
      return "Watch";
    case "brand":
      return "On track";
    case "calm":
      return "Calm";
    default:
      return "—";
  }
}

/* ----------------------------------------------------------------- */
/* Holo Button — premium primary CTA in the Lumi card language.       */
/* ----------------------------------------------------------------- */

export const ParentHoloButton = ({
  children,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    {...rest}
    className={cn(
      "group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-2xl",
      "border border-primary/40 bg-gradient-to-r from-primary/95 via-primary to-secondary",
      "px-5 py-3 text-sm font-semibold tracking-tight text-primary-foreground",
      "shadow-[0_12px_32px_-12px_hsl(var(--primary)/0.7),inset_0_1px_0_0_hsl(var(--foreground)/0.18)]",
      "transition-all hover:brightness-110 active:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
      "disabled:cursor-not-allowed disabled:opacity-60",
      className,
    )}
  >
    <span className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
    {children}
  </button>
);

/* ----------------------------------------------------------------- */
/* Destructive action — quiet outline → confirm → destructive.        */
/* ----------------------------------------------------------------- */

export function ParentDestructiveAction({
  triggerLabel,
  title,
  description,
  confirmLabel = "Remove",
  onConfirm,
  disabled,
}: {
  triggerLabel: string;
  title: string;
  description: string;
  confirmLabel?: string;
  onConfirm: () => void | Promise<void>;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="h-8 gap-1.5 rounded-full border-white/10 bg-background/40 px-3 text-[11px] font-medium text-muted-foreground backdrop-blur hover:border-rose-400/30 hover:bg-rose-500/[0.06] hover:text-rose-200"
        >
          {triggerLabel}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={async () => {
              await onConfirm();
              setOpen(false);
            }}
            className="bg-rose-500/90 text-white hover:bg-rose-500"
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ----------------------------------------------------------------- */
/* Empty hint — premium low-data state with chips.                    */
/* ----------------------------------------------------------------- */

export function ParentEmptyHint({
  title,
  body,
  chips = [],
  icon: Icon,
}: {
  title: string;
  body: string;
  chips?: { label: string; value: string }[];
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/[0.05] bg-gradient-to-br from-background/40 to-background/10 p-5 backdrop-blur-md">
      <div className="pointer-events-none absolute -top-10 -right-10 h-24 w-24 rounded-full bg-primary/8 blur-2xl" />
      <div className="relative flex items-start gap-3">
        {Icon && (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/25">
            <Icon className="h-3.5 w-3.5" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-semibold text-foreground">{title}</p>
          <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">{body}</p>
          {chips.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {chips.map((c) => (
                <span
                  key={c.label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.07] bg-background/40 px-2.5 py-1 text-[10.5px] font-medium text-foreground/90 backdrop-blur"
                >
                  <span className="text-muted-foreground">{c.label}</span>
                  <span>{c.value}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- */
/* Tab bar — premium holo control bar replacing shadcn TabsList.      */
/* ----------------------------------------------------------------- */

export function ParentTabBar({
  items,
  active,
  onSelect,
  className,
}: {
  items: { value: string; label: string; icon?: React.ComponentType<{ className?: string }> }[];
  active: string;
  onSelect: (v: string) => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative overflow-x-auto rounded-2xl border border-white/[0.06] ring-1 ring-inset ring-white/[0.03]",
        "bg-[linear-gradient(180deg,hsl(var(--card)/0.7)_0%,hsl(var(--card)/0.4)_100%)] p-1.5 backdrop-blur-2xl",
        "shadow-[0_14px_36px_-26px_rgba(0,0,0,0.6),inset_0_1px_0_0_hsl(var(--foreground)/0.05)]",
        className,
      )}
    >
      <div className="flex min-w-max items-center gap-1">
        {items.map((it) => {
          const isActive = it.value === active;
          const Icon = it.icon;
          return (
            <button
              key={it.value}
              type="button"
              onClick={() => onSelect(it.value)}
              className={cn(
                "relative inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[12.5px] font-medium tracking-tight transition-all sm:text-[13px]",
                isActive
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-pressed={isActive}
            >
              {isActive && (
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-xl bg-[linear-gradient(180deg,hsl(var(--foreground)/0.10)_0%,hsl(var(--foreground)/0.04)_100%)] ring-1 ring-white/[0.08] shadow-[0_6px_18px_-10px_rgba(0,0,0,0.6),inset_0_1px_0_0_hsl(var(--foreground)/0.10)]"
                />
              )}
              {Icon && <Icon className={cn("relative h-3.5 w-3.5", isActive ? "text-primary" : "")} />}
              <span className="relative">{it.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

type ChipTone = "brand" | "calm" | "growth" | "ready" | "neutral" | "watch";
const CHIP_CLS: Record<ChipTone, string> = {
  brand: "bg-primary/10 text-primary border-primary/30",
  calm: "bg-sky-500/10 text-sky-200 border-sky-400/30",
  ready: "bg-emerald-500/10 text-emerald-200 border-emerald-400/30",
  growth: "bg-amber-500/10 text-amber-200 border-amber-400/30",
  watch: "bg-rose-500/10 text-rose-200 border-rose-400/30",
  neutral: "bg-muted/30 text-muted-foreground border-border/40",
};

export function ParentStatusChip({ tone = "brand", label }: { tone?: ChipTone; label: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium tracking-wide backdrop-blur-md",
        CHIP_CLS[tone],
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
      {label}
    </span>
  );
}

export function ParentEmptyState({
  title,
  body,
  icon: Icon,
}: {
  title: string;
  body: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-white/[0.05] bg-background/30 px-6 py-8 text-center backdrop-blur-md">
      {Icon && (
        <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/25">
          <Icon className="h-4 w-4" />
        </span>
      )}
      <p className="font-heading text-sm font-semibold text-foreground">{title}</p>
      <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

export function ParentSectionTabsList({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "relative overflow-x-auto rounded-2xl border border-white/[0.07] bg-gradient-to-br from-card/80 via-card/60 to-card/40 p-1.5 backdrop-blur-2xl shadow-[0_16px_40px_-30px_hsl(var(--primary)/0.45),inset_0_1px_0_0_hsl(var(--foreground)/0.05)]",
        className,
      )}
    >
      <div className="flex min-w-max items-center gap-1">{children}</div>
    </div>
  );
}